import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Pagination,
  keyframes,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import { DropDownArrowIcon } from "@ippon/shared-assets";
import { CustomTruncateText, CustomCheckbox } from "../../index";

export interface TableProps {
  rows: any[];
  columns: any[];
  checkbox?: any;
  selectedIds?: string[];
  onRowSelection?: (selectedIds: string[]) => void;
  onAdminStatusClick?: (row: any, newStatus: boolean) => void;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
}

interface MobileTableProps extends TableProps {
  isLoading?: boolean;
  pagination?: PaginationInfo;
  checkBoxOpen?: boolean;
  onPageChange?: (page: number) => void;
  onRowSelection?: (selectedIds: any[]) => void;
  selectedIds?: any[];
  handlePropertyClick?: (row: any) => void; // Add the click handler prop
  disableNameClick?: boolean; // Add prop to disable name clicking
  hidePagination?: boolean; // Add prop to hide pagination
  pageSize?: number; // Add pageSize prop
  getRowSx?: (row: any) => Record<string, any> | undefined;
  renderTitle?: (row: any) => React.ReactNode;
}

// ラベル幅の見積り（fontSize 14px を前提）
// 全角と半角で文字幅が倍近く違うため、文字数だけで見積もると
// 日本語のラベル（例:「合計金額（税込）」）が収まらず2行に折り返される。
const FULL_WIDTH_CHAR_PX = 14; // 全角（かな・漢字・全角記号）
const HALF_WIDTH_CHAR_PX = 8; // 半角（英数字・半角記号）
const LABEL_PADDING_PX = 16; // 左右の内余白

/** 全角文字か（かな・漢字・全角記号・全角英数） */
const isFullWidthChar = (char: string): boolean =>
  /[ᄀ-ᅟ⺀-〾ぁ-㏿㐀-䶿一-鿿가-힣豈-﫿︰-﹯＀-｠￠-￦]/.test(
    char
  );

/** ラベルの表示幅をピクセルで見積もる */
const estimateLabelWidth = (label: string): number =>
  Array.from(label).reduce(
    (width, char) =>
      width + (isFullWidthChar(char) ? FULL_WIDTH_CHAR_PX : HALF_WIDTH_CHAR_PX),
    0
  );

// Loading spinner animation
const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Custom Loading Overlay
const CustomLoadingOverlay = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      zIndex: 10,
      minHeight: "300px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <Box
        sx={{
          width: "50px",
          height: "50px",
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #0B9DBD",
          borderRadius: "50%",
          animation: `${spinAnimation} 1s linear infinite`,
        }}
      />
    </Box>
  </Box>
);

const MobileTable: React.FC<MobileTableProps> = ({
  rows,
  columns,
  onRowSelection,
  selectedIds = [],
  isLoading = false,
  pagination,
  checkBoxOpen = true,
  onPageChange,
  handlePropertyClick, // Accept the click handler
  disableNameClick = false, // Accept the disable click prop
  hidePagination = false, // Accept the hide pagination prop
  pageSize: pageSizeProp, // Accept the pageSize prop
  getRowSx,
  renderTitle,
}) => {
  const [localPage, setLocalPage] = useState(1);

  // Calculate the maximum label width dynamically
  const getMaxLabelWidth = () => {
    const visibleColumns = columns.filter(
      (col) =>
        col.field !== "name" &&
        col.field !== "id" &&
        col.field !== "report_title"
    );
    const labelWidths = visibleColumns.map((column) =>
      estimateLabelWidth(column?.headerName || column.field)
    );
    const widest = Math.max(...labelWidths);
    // Use 35% of screen width as maximum to prevent overflow
    const calculatedWidth = Math.max(100, widest + LABEL_PADDING_PX); // minimum 100px
    const maxAllowedWidth = Math.min(calculatedWidth, window.innerWidth * 0.35);
    return maxAllowedWidth;
  };

  const maxLabelWidth = getMaxLabelWidth();

  const handleSelectionChange = (rowId: number, checked: boolean) => {
    if (!onRowSelection) return;

    let newSelectedIds: any[];
    if (checked) {
      newSelectedIds = [...(selectedIds || []), rowId];
    } else {
      newSelectedIds = (selectedIds || []).filter((id: any) => id !== rowId);
    }
    onRowSelection(newSelectedIds);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (pagination && onPageChange) {
      onPageChange(value);
    } else {
      setLocalPage(value);
    }
  };

  // Calculate pagination values
  const getPaginationValues = () => {
    if (pagination) {
      return {
        totalPages: Math.ceil(pagination.total / pagination.limit),
        currentPage: pagination.page,
        showPagination: pagination.total > pagination.limit,
      };
    } else {
      const pageSize = pageSizeProp ?? 10;
      return {
        totalPages: Math.ceil(rows.length / pageSize),
        currentPage: localPage,
        showPagination: rows.length > pageSize,
      };
    }
  };

  const { totalPages, currentPage, showPagination } = getPaginationValues();

  // Calculate displayed rows for pagination
  const displayedRows = React.useMemo(() => {
    if (pagination || hidePagination) {
      return rows;
    }
    const pageSize = pageSizeProp ?? 10;
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return rows.slice(start, end);
  }, [rows, pagination, hidePagination, currentPage, pageSizeProp]);

  // Get field mappings from columns
  const getFieldLabel = (field: string) => {
    const column = columns.find((col) => col.field === field);
    return column?.headerName || field;
  };

  // Render field value based on column configuration
  const renderFieldValue = (row: any, field: string) => {
    const column = columns.find((col) => col.field === field);
    if (column?.renderCell) {
      return column.renderCell({ row, value: row[field] });
    }
    return row[field];
  };

  // Handle name click for navigation
  const handleNameClick = (row: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion toggle
    if (handlePropertyClick) {
      handlePropertyClick(row);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isLoading && <CustomLoadingOverlay />}

      {/* All items as collapsed accordions by default */}
      {displayedRows.map((row, index) => (
        <Card
          key={row.id || index}
          sx={{
            marginBottom: 1,
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            width: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Accordion
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
              "&.MuiAccordion-root": { margin: 0 },
              width: "100%",
              overflow: "hidden",
            }}
            defaultExpanded={false} // All items collapsed by default
          >
            <AccordionSummary
              expandIcon={
                <DropDownArrowIcon
                  sx={{ width: "14px", height: "14px", flexShrink: 0 }}
                />
              }
              sx={{
                padding: "8px 16px",
                minHeight: "40px !important",
                height: "40px !important",
                width: "100%",
                overflow: "hidden",
                "&.Mui-expanded": {
                  backgroundColor: "#F0F8FF",
                },
                backgroundColor: selectedIds?.includes(row.id)
                  ? "#F0F8FF"
                  : "transparent",
                ...(getRowSx ? getRowSx(row) : {}),
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  margin: "0px !important",
                  overflow: "hidden",
                  width: "100%",
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "0px !important",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  flexShrink: 0,
                  marginLeft: "8px",
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {checkBoxOpen && (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      marginRight: 1,
                      flexShrink: 0,
                    }}
                  >
                    <CustomCheckbox
                      checked={selectedIds?.includes(row.id) || false}
                      onChange={() => {
                        const isChecked =
                          selectedIds?.includes(row.id) || false;
                        handleSelectionChange(row.id, !isChecked);
                      }}
                      size="small"
                    />
                  </Box>
                )}

                {/* Clickable name that doesn't trigger accordion */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: checkBoxOpen
                      ? "calc(100% - 50px)"
                      : "calc(100% - 20px)", // より多くの文字を表示できるように調整
                    overflow: "hidden",
                    paddingRight: 1, // アイコンとの間により多くのスペースを確保
                  }}
                >
                  {renderTitle ? (
                    renderTitle(row)
                  ) : (
                    <Typography
                      variant="body1"
                      onClick={
                        disableNameClick
                          ? undefined
                          : (e) => handleNameClick(row, e)
                      }
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        textDecoration: disableNameClick ? "none" : "underline",
                        cursor: disableNameClick ? "default" : "pointer",
                        color: "#3e3e3e",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      <CustomTruncateText
                        text={
                          row.title ||
                          row.propertyName ||
                          row.customerName ||
                          row.customer_name ||
                          row.name ||
                          (row.firstName && row.lastName
                            ? `${row.lastName} ${row.firstName}`
                            : "") ||
                          String(row.id || "")
                        }
                      />
                    </Typography>
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                padding: "0px 16px 2px 16px",
                width: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {columns
                .filter(
                  (col) =>
                    col.field !== "name" &&
                    col.field !== "id" &&
                    col.field !== "report_title"
                )
                .map((column, fieldIndex) => (
                  <Box
                    key={column.field}
                    sx={{
                      display: "flex",
                      paddingY: "5px",
                      borderBottom:
                        fieldIndex < columns.length - 2
                          ? "1px solid #f0f0f0"
                          : "none",
                      width: "100%",
                      overflow: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: `${maxLabelWidth}px`,
                        maxWidth: `${maxLabelWidth}px`,
                        width: `${maxLabelWidth}px`,
                        fontWeight: 500,
                        color: "#666",
                        backgroundColor: "#f9f9f9",
                        padding: "4px 8px",
                        borderRadius: "8px",
                        marginRight: 2,
                        flexShrink: 0,
                        fontSize: "14px",
                        lineHeight: 1.2,
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        wordBreak: "break-all",
                      }}
                    >
                      {getFieldLabel(column.field)}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        paddingY: "4px",
                        minWidth: 0,
                        // 全てのフィールドでoverflow: visibleに
                        overflow: "visible",
                      }}
                    >
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          lineHeight: 1.2,
                          // 全てのフィールドで折り返し表示
                          whiteSpace: "normal",
                          textOverflow: "clip",
                          overflow: "visible",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-all",
                          "& > *": {
                            maxWidth: "100%",
                            fontSize: "14px",
                          },
                          "& .MuiFormControl-root": {
                            maxWidth: "100%",
                            minWidth: "120px",
                            width: "100%",
                          },
                          "& .MuiSelect-select": {
                            fontSize: "14px",
                            padding: "4px 8px",
                            minHeight: "20px",
                          },
                          "& .MuiOutlinedInput-root": {
                            minHeight: "32px",
                          },
                        }}
                      >
                        {renderFieldValue(row, column.field)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </AccordionDetails>
          </Accordion>
        </Card>
      ))}

      {/* Pagination */}
      {!hidePagination && showPagination && totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Pagination
            shape="rounded"
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            disabled={isLoading}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                backgroundColor: "#F5F5F5",
                color: "#000",
                borderRadius: "6px",
                "&.Mui-selected": {
                  backgroundColor: "#039dbe",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#039dbe",
                  },
                },
                "&:hover": {
                  backgroundColor: "#DBF8FF",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F5F5F5",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MobileTable;
